// Pure, framework-free helpers for the live locator map. Extracted so the
// deterministic boundaries (coordinate extraction, GeoJSON munging, coords-bar
// label formatting) can be unit tested without a live Mapbox instance.

/** GeoJSON position: [lng, lat] (optionally with elevation as a 3rd element). */
export type LngLatPosition = [number, number] | [number, number, number];

/**
 * Return the last coordinate of a LineString-style GeoJSON feature, or null if
 * the feature has no usable geometry. Used both to place the "current position"
 * pulsing dot and to decide the initial fly-to target.
 */
export function lastCoordinate(geoJson: any): LngLatPosition | null {
  const coords = geoJson?.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  const last = coords[coords.length - 1];
  if (!Array.isArray(last) || last.length < 2) return null;
  return last as LngLatPosition;
}

/**
 * Build the single-point FeatureCollection that backs the pulsing "current
 * position" dot source.
 */
export function buildDotPointFeatureCollection(lastPoint: LngLatPosition) {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: lastPoint,
        },
        properties: {},
      },
    ],
  };
}

/**
 * Compose the coords-bar label from the (already converted) grid-ref string and
 * an optional elevation reading, matching the format used by the main map page
 * (`"<coords> <elevation>"`). Falls back to coords-only when elevation is
 * missing so the bar never renders a dangling trailing space or "undefined".
 */
export function buildCoordsLabel(coords?: string | null, elevation?: string | null): string {
  const c = (coords ?? "").toString().trim();
  const e = (elevation ?? "").toString().trim();
  if (!c) return e;
  return e ? `${c} ${e}` : c;
}
